"use client"

import React, { useState, useEffect, useCallback } from "react"
import ReactFlow, {
  ReactFlowProvider,
  Controls,
  Background,
} from "reactflow"
import "reactflow/dist/style.css"
import "./SkillMasteryPage.css"

function updateBKT(pPrev, correct, { learn, guess, slip }) {
  // P(L_n | correct)
  if (correct) {
    const num = pPrev * (1 - slip)
    const den = num + (1 - pPrev) * guess
    const pCorrect = num / den
    return pCorrect + (1 - pCorrect) * learn
  } else {
    // incorrect
    const num = pPrev * slip
    const den = pPrev * slip + (1 - pPrev) * (1 - guess)
    const pIncorrect = num / den
    return pIncorrect + (1 - pIncorrect) * learn
  }
}

const DEFAULT_PARAMS = {
  init: 0.2,
  learn: 0.15,
  guess: 0.2,
  slip: 0.1,
}

const SKILLS = [
  { id: "s1", label: "SELECT" },
  { id: "s2", label: "FROM" },
  { id: "s3", label: "WHERE" },
  { id: "s4", label: "GROUP BY" },
  { id: "s5", label: "HAVING" },
]
const PREREQS = [
  { source: "s1", target: "s2" },
  { source: "s2", target: "s3" },
  { source: "s3", target: "s4" },
  { source: "s4", target: "s5" },
]

const HISTORY = [
  { skill: "s1", correct: true },
  { skill: "s2", correct: false },
  { skill: "s2", correct: true },
  { skill: "s3", correct: false },
  { skill: "s3", correct: false },
  { skill: "s3", correct: true },
  { skill: "s4", correct: false },
]

function SkillMasteryPage() {
  const [params, setParams] = useState(DEFAULT_PARAMS)
  const [, setMastery] = useState({})
  const [nodes, setNodes] = useState([])
  const [edges, setEdges] = useState([])

  // Run BKT over history and compute final mastery per skill
  const computeMastery = useCallback(() => {
    // initialize all skills
    const p = SKILLS.reduce((acc, s) => {
      acc[s.id] = params.init
      return acc
    }, {})
    // apply history in order
    HISTORY.forEach(({ skill, correct }) => {
      p[skill] = updateBKT(p[skill], correct, params)
    })
    return p
  }, [params])

  useEffect(() => {
    // compute mastery
    const p = computeMastery()
    setMastery(p)
    // build nodes with coloring by p
    const flowNodes = SKILLS.map((s, i) => {
      const prob = p[s.id]
      // interpolate color from red to green
      const r = Math.round((1 - prob) * 255)
      const g = Math.round(prob * 255)
      const bg = `rgb(${r},${g},50)`
      return {
        id: s.id,
        data: { label: `${s.label}\n${(prob*100).toFixed(1)}%` },
        position: { x: 150 + i * 200, y: 100 + (i % 2) * 150 },
        style: { background: bg, color: "#fff", padding: 10, borderRadius: 5 },
      }
    })
    setNodes(flowNodes)

    // build edges
    setEdges(PREREQS.map((e, i) => ({
      id: `e${i}`,
      source: e.source,
      target: e.target,
      animated: true,
      label: "prereq",
    })))
  }, [computeMastery])
  // handle param changes
  const handleChange = (e) => {
    const { name, value } = e.target
    setParams((p) => ({ ...p, [name]: parseFloat(value) }))
  }

  return (
    <div className="skill-master-container">
      <div className="skill-master-sidebar">
        <h2>BKT Parameters</h2>
        {["init","learn","guess","slip"].map((k) => (
          <div key={k} className="param-row">
            <label>{k}</label>
            <input
              type="number"
              step="0.01" min="0" max="1"
              name={k}
              value={params[k]}
              onChange={handleChange}
            />
          </div>
        ))}
        <button onClick={() => setParams({ ...params })}>
          Recompute Mastery
        </button>
      </div>
      <div className="skill-master-graph">
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            fitView
          >
            <Controls />
            <Background variant="dots" gap={12} size={1} />
          </ReactFlow>
        </ReactFlowProvider>
      </div>
    </div>
  )
}

export default SkillMasteryPage
